/**
 * fc-trustpilot — initialises a Trustpilot TrustBox widget inside a section.
 *
 * Trustpilot's bootstrap script only scans the DOM once, on its own load. That breaks in two
 * places we care about: (a) the theme editor, which re-renders a section long after the script
 * has run, and (b) a page where the Trustpilot Shopify app has ALREADY loaded the bootstrap —
 * our widget div appears after the scan and stays empty. So instead of relying on the scan we
 * drive it explicitly via Trustpilot.loadFromElement(), and only inject the bootstrap ourselves
 * if nothing else on the page has.
 *
 * Fail-safe: if the script never loads (blocked, offline, consent-gated), the plain <a> inside
 * the widget div stays visible as a normal link to the profile. Nothing breaks.
 */

const BOOTSTRAP_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

class FcTrustpilot extends HTMLElement {
  connectedCallback() {
    this.widget = this.querySelector('.trustpilot-widget');
    if (!this.widget) return;
    this.#render();
  }

  #render() {
    // Bootstrap already present and ready (usually the Trustpilot app, or another instance of
    // this section further up the page) — just ask it to build this one element.
    if (window.Trustpilot?.loadFromElement) {
      window.Trustpilot.loadFromElement(this.widget, true);
      return;
    }

    let script = document.querySelector('script[src*="tp.widget.bootstrap"]');

    if (!script) {
      script = document.createElement('script');
      script.src = BOOTSTRAP_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    // Covers both "we just injected it" and "it's in the DOM but still loading".
    script.addEventListener(
      'load',
      () => {
        window.Trustpilot?.loadFromElement?.(this.widget, true);
      },
      { once: true }
    );
  }
}

if (!customElements.get('fc-trustpilot')) {
  customElements.define('fc-trustpilot', FcTrustpilot);
}
